import type { NoTrainingConfirmation, NoTrainingStatus } from '../services/trainingLogApi';

/**
 * State logic of the "no training" banner (training reminder B1).
 * The mail link only opens the page; nothing is written on load. The single write is the POST
 * behind the button click (masterplan §2, E1, E11).
 */

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/** Valid YYYY-MM (month 01-12) or null. Anything else in the query string is ignored. */
export function parseConfirmMonth(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(raw.trim());
    return m ? `${m[1]}-${m[2]}` : null;
}

/** "2026-10" -> "October 2026"; unknown input is returned unchanged. */
export function formatConfirmMonth(month: string): string {
    const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
    return m ? `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : month;
}

export type BannerState =
    | { kind: 'loading' }
    | { kind: 'load-error'; message: string }
    /** Status loaded, nothing confirmed, no training: the button is offered. */
    | { kind: 'ask'; month: string }
    | { kind: 'confirming'; month: string }
    | { kind: 'confirm-error'; month: string; message: string }
    /** Confirmed (now or earlier). */
    | { kind: 'confirmed'; month: string; confirmedAt: string }
    /** The person has trainings in the month: shown instead of the button. */
    | { kind: 'has-training'; month: string; trainingCount: number }
    /** Month lies after the current month (Europe/Zurich): it cannot be confirmed yet. */
    | { kind: 'future'; month: string }
    /** Month is neither current nor previous (E11) and nothing to show. */
    | { kind: 'closed'; month: string };

/** Current month as YYYY-MM in Europe/Zurich (never toISOString, which is UTC). */
export function currentMonthZurich(now: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit' })
        .format(now).slice(0, 7);
}

/** Derive the state from the server status. Trainings win over a confirmation (E11). */
export function stateFromStatus(status: NoTrainingStatus, now: Date = new Date()): BannerState {
    if (status.trainingCount > 0) {
        return { kind: 'has-training', month: status.month, trainingCount: status.trainingCount };
    }
    if (status.confirmedAt) {
        return { kind: 'confirmed', month: status.month, confirmedAt: status.confirmedAt };
    }
    if (status.canConfirm) return { kind: 'ask', month: status.month };
    // YYYY-MM strings compare correctly as text.
    if (status.month > currentMonthZurich(now)) return { kind: 'future', month: status.month };
    return { kind: 'closed', month: status.month };
}

export interface NoTrainingApi {
    getNoTrainingStatus(month: string): Promise<NoTrainingStatus>;
    confirmNoTraining(month: string): Promise<NoTrainingConfirmation>;
}

const errorMessage = (e: unknown, fallback: string): string =>
    e instanceof Error && e.message ? e.message : fallback;

/**
 * Drives one banner. `load()` does a GET only. `confirm()` does the POST, only from state 'ask'
 * or 'confirm-error' (retry), and at most one at a time: a second click while the first is in
 * flight, or after success, sends nothing.
 */
export function createNoTrainingConfirmer(
    api: NoTrainingApi,
    month: string,
    onChange: (state: BannerState) => void,
) {
    let state: BannerState = { kind: 'loading' };
    let inFlight = false;
    const set = (s: BannerState) => { state = s; onChange(s); };

    return {
        getState: () => state,

        async load(): Promise<void> {
            set({ kind: 'loading' });
            try {
                set(stateFromStatus(await api.getNoTrainingStatus(month)));
            } catch (e) {
                set({ kind: 'load-error', message: errorMessage(e, 'Could not load the status.') });
            }
        },

        async confirm(): Promise<void> {
            if (inFlight) return;
            if (state.kind !== 'ask' && state.kind !== 'confirm-error') return;
            inFlight = true;
            set({ kind: 'confirming', month });
            try {
                const res = await api.confirmNoTraining(month);
                set({ kind: 'confirmed', month, confirmedAt: res.confirmedAt });
            } catch (e) {
                set({ kind: 'confirm-error', month, message: errorMessage(e, 'Could not save the confirmation.') });
            } finally {
                inFlight = false;
            }
        },
    };
}
